import { useState, useEffect, useCallback } from 'react';

/**
 * Reusable hook for modal discard protection
 * 
 * Provides consistent behavior across all modal screens:
 * - Tracks unsaved changes
 * - Handles close attempts (X button, overlay click, Esc key)
 * - Shows discard confirmation dialog when needed
 * 
 * Usage:
 * ```tsx
 * const {
 *   hasUnsavedChanges,
 *   showDiscardDialog,
 *   markDirty,
 *   handleClose,
 *   confirmDiscard,
 *   cancelDiscard,
 * } = useModalGuard({
 *   onClose: () => props.onClose(),
 * });
 * ```
 */

interface UseModalGuardOptions {
  /** Callback to execute when modal should close */
  onClose: () => void;
  
  /** Whether to enable Esc key handler (default: true) */
  enableEscKey?: boolean;
  
  /** Whether to enable beforeunload warning (default: false - can be annoying) */
  enableBeforeUnload?: boolean;
}

export interface ModalGuardState {
  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean;
  
  /** Whether the discard confirmation dialog should be shown */
  showDiscardDialog: boolean;
  
  /** Mark the form as dirty (has unsaved changes) */
  markDirty: () => void;
  
  /** Mark the form as clean (no unsaved changes) */
  markClean: () => void;
  
  /** Handle close attempt - will show dialog if dirty */
  handleClose: () => void;
  
  /** Confirm discard and close */
  confirmDiscard: () => void;
  
  /** Cancel discard and keep editing */
  cancelDiscard: () => void;
}

export function useModalGuard(options: UseModalGuardOptions): ModalGuardState {
  const { 
    onClose, 
    enableEscKey = true, 
    enableBeforeUnload = false 
  } = options;
  
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const markDirty = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const markClean = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const confirmDiscard = useCallback(() => {
    setShowDiscardDialog(false);
    setHasUnsavedChanges(false);
    onClose();
  }, [onClose]);

  const cancelDiscard = useCallback(() => {
    setShowDiscardDialog(false);
  }, []);

  // Esc key handler
  useEffect(() => {
    if (!enableEscKey) return;
    
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [enableEscKey, handleClose]);

  // beforeunload handler (for browser back/navigation)
  useEffect(() => {
    if (!enableBeforeUnload) return;
    
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enableBeforeUnload, hasUnsavedChanges]);

  return {
    hasUnsavedChanges,
    showDiscardDialog,
    markDirty,
    markClean,
    handleClose,
    confirmDiscard,
    cancelDiscard,
  };
}
